/**
 * ⚔️ WEAPONS
 *
 * All weapon equipment items (21 total)
 * Level requirements: 5-105 (increments of 5)
 * Stat requirements: STR for melee, DEX for ranged
 *
 * Level Distribution:
 * - Common (3): Lv 5, 10, 15
 * - Uncommon (3): Lv 20, 25, 30
 * - Rare (3): Lv 35, 40, 45
 * - Epic (3): Lv 50, 55, 60
 * - Legendary (6): Lv 65, 70, 75, 80, 85, 90
 * - Mythic (6): Lv 80, 85, 90, 95, 100, 105
 */

export const WEAPONS = {
  // ============================================================
  // COMMON WEAPONS (3) - Lv 5, 10, 15
  // ============================================================
  rustyDagger: {
    id: 'rustyDagger',
    name: 'Rusty Dagger',
    icon: '🗡️',
    slot: 'weapon',
    rarity: 'common',
    levelReq: 5,
    requirements: { DEX: 8 },
    description: 'A worn dagger, barely sharp enough to cut.',
    stats: {
      attack: 8,
      critChance: 0.03,
    },
  },
  woodenClub: {
    id: 'woodenClub',
    name: 'Wooden Club',
    icon: '🏏',
    slot: 'weapon',
    rarity: 'common',
    levelReq: 10,
    requirements: { STR: 15 },
    description: 'A simple club carved from hardwood.',
    stats: {
      attack: 10,
      critChance: 0.02,
    },
  },
  brokenSword: {
    id: 'brokenSword',
    name: 'Broken Sword',
    icon: '⚔️',
    slot: 'weapon',
    rarity: 'common',
    levelReq: 15,
    requirements: { STR: 20 },
    description: 'Half a blade is better than none.',
    stats: {
      attack: 12,
      critChance: 0.04,
    },
  },

  // ============================================================
  // UNCOMMON WEAPONS (3) - Lv 20, 25, 30
  // ============================================================
  steelSword: {
    id: 'steelSword',
    name: 'Steel Sword',
    icon: '⚔️',
    slot: 'weapon',
    rarity: 'uncommon',
    levelReq: 20,
    requirements: { STR: 35 },
    description: 'A well-crafted blade of tempered steel.',
    stats: {
      attack: 18,
      critChance: 0.06,
      critDamage: 0.20,
    },
  },
  huntingBow: {
    id: 'huntingBow',
    name: 'Hunting Bow',
    icon: '🏹',
    slot: 'weapon',
    rarity: 'uncommon',
    levelReq: 25,
    requirements: { DEX: 40 },
    description: 'A reliable bow for hunting game... or enemies.',
    stats: {
      attack: 16,
      critChance: 0.10,
      critDamage: 0.15,
    },
  },
  warHammer: {
    id: 'warHammer',
    name: 'War Hammer',
    icon: '🔨',
    slot: 'weapon',
    rarity: 'uncommon',
    levelReq: 30,
    requirements: { STR: 50 },
    description: 'Crushes armor and bones with equal ease.',
    stats: {
      attack: 20,
      critChance: 0.05,
      critDamage: 0.25,
    },
  },

  // ============================================================
  // RARE WEAPONS (3) - Lv 35, 40, 45
  // ============================================================
  flamingSword: {
    id: 'flamingSword',
    name: 'Flaming Sword',
    icon: '🔥',
    slot: 'weapon',
    rarity: 'rare',
    levelReq: 35,
    requirements: { STR: 65 },
    description: 'A blade wreathed in eternal flames.',
    stats: {
      attack: 30,
      critChance: 0.10,
      critDamage: 0.40,
    },
  },
  crystalBow: {
    id: 'crystalBow',
    name: 'Crystal Bow',
    icon: '🏹',
    slot: 'weapon',
    rarity: 'rare',
    levelReq: 40,
    requirements: { DEX: 75 },
    description: 'Arrows fired from this bow never miss their mark.',
    stats: {
      attack: 28,
      critChance: 0.15,
      critDamage: 0.45,
    },
  },
  thunderAxe: {
    id: 'thunderAxe',
    name: 'Thunder Axe',
    icon: '⚡',
    slot: 'weapon',
    rarity: 'rare',
    levelReq: 45,
    requirements: { STR: 85 },
    description: 'Each strike echoes with the sound of thunder.',
    stats: {
      attack: 32,
      critChance: 0.10,
      critDamage: 0.50,
    },
  },

  // ============================================================
  // EPIC WEAPONS (3) - Lv 50, 55, 60
  // ============================================================
  dragonslayerBlade: {
    id: 'dragonslayerBlade',
    name: 'Dragonslayer Blade',
    icon: '⚔️',
    slot: 'weapon',
    rarity: 'epic',
    levelReq: 50,
    requirements: { STR: 100 },
    description: 'Forged to slay dragons, feared by all creatures.',
    stats: {
      attack: 55,
      critChance: 0.15,
      critDamage: 0.75,
    },
  },
  voidTrident: {
    id: 'voidTrident',
    name: 'Void Trident',
    icon: '🔱',
    slot: 'weapon',
    rarity: 'epic',
    levelReq: 55,
    requirements: { STR: 90, INT: 45 },
    description: 'A weapon from beyond reality itself.',
    stats: {
      attack: 50,
      critChance: 0.20,
      critDamage: 0.65,
    },
  },
  starfallenBow: {
    id: 'starfallenBow',
    name: 'Starfallen Bow',
    icon: '🏹',
    slot: 'weapon',
    rarity: 'epic',
    levelReq: 60,
    requirements: { DEX: 120 },
    description: 'Its arrows are fragments of fallen stars.',
    stats: {
      attack: 48,
      critChance: 0.22,
      critDamage: 0.70,
    },
  },

  // ============================================================
  // LEGENDARY WEAPONS (6) - Lv 65, 70, 75, 80, 85, 90
  // ============================================================
  soulreaver: {
    id: 'soulreaver',
    name: 'Soulreaver',
    icon: '⚔️',
    slot: 'weapon',
    rarity: 'legendary',
    levelReq: 65,
    requirements: { STR: 130, LUK: 40 },
    description: 'A blade that devours the souls of the slain.',
    stats: {
      attack: 85,
      critChance: 0.25,
      critDamage: 1.20,
    },
  },
  cosmicScythe: {
    id: 'cosmicScythe',
    name: 'Cosmic Scythe',
    icon: '🌌',
    slot: 'weapon',
    rarity: 'legendary',
    levelReq: 70,
    requirements: { STR: 120, INT: 60 },
    description: 'Wielded by the harbinger of the end times.',
    stats: {
      attack: 82,
      critChance: 0.28,
      critDamage: 1.10,
    },
  },
  infinityStaff: {
    id: 'infinityStaff',
    name: 'Infinity Staff',
    icon: '✨',
    slot: 'weapon',
    rarity: 'legendary',
    levelReq: 75,
    requirements: { INT: 150 },
    description: 'Channels the infinite power of creation itself.',
    stats: {
      attack: 75,
      critChance: 0.30,
      critDamage: 1.30,
    },
  },
  voidBlade: {
    id: 'voidBlade',
    name: 'Void Blade',
    icon: '🌑',
    slot: 'weapon',
    rarity: 'legendary',
    levelReq: 80,
    requirements: { STR: 160, INT: 50 },
    description: 'A blade forged from crystallized void energy.',
    stats: {
      attack: 90,
      critChance: 0.22,
      critDamage: 1.15,
      defense: 15,
    },
  },
  starforgedAxe: {
    id: 'starforgedAxe',
    name: 'Starforged Axe',
    icon: '⚡',
    slot: 'weapon',
    rarity: 'legendary',
    levelReq: 85,
    requirements: { STR: 180 },
    description: 'An axe forged in the heart of a supernova.',
    stats: {
      attack: 95,
      critChance: 0.20,
      critDamage: 1.40,
    },
  },
  eternityBow: {
    id: 'eternityBow',
    name: 'Eternity Bow',
    icon: '🏹',
    slot: 'weapon',
    rarity: 'legendary',
    levelReq: 90,
    requirements: { DEX: 190 },
    description: 'Its arrows pierce through time itself.',
    stats: {
      attack: 88,
      critChance: 0.26,
      critDamage: 1.35,
      speed: 1,
    },
  },

  // ============================================================
  // MYTHIC WEAPONS (6) - Lv 80, 85, 90, 95, 100, 105
  // ============================================================
  firebrand: {
    id: 'firebrand',
    name: 'Firebrand',
    icon: '🔥',
    sprite: 'firebrand.png',
    slot: 'weapon',
    rarity: 'mythic',
    levelReq: 80,
    requirements: { STR: 170, INT: 60 },
    description: 'A legendary blade forged in the heart of a dying star. Burns with eternal hellfire.',
    stats: {
      attack: 160,
      critChance: 0.40,
      critDamage: 2.50,
      health: 300,
    },
  },
  frostblade: {
    id: 'frostblade',
    name: 'Frostblade',
    icon: '❄️',
    sprite: 'frostblade.png',
    slot: 'weapon',
    rarity: 'mythic',
    levelReq: 85,
    requirements: { STR: 150, DEX: 80 },
    description: 'An ancient blade frozen in eternal ice. Slows time itself with each strike.',
    stats: {
      attack: 150,
      critChance: 0.45,
      critDamage: 2.20,
      defense: 40,
      speed: 2,
    },
  },
  natureweaver: {
    id: 'natureweaver',
    name: 'Natureweaver',
    icon: '🌿',
    sprite: 'natureweaver.png',
    slot: 'weapon',
    rarity: 'mythic',
    levelReq: 90,
    requirements: { INT: 200, LUK: 60 },
    description: 'A living staff that commands the forces of nature. Heals the wielder with each strike.',
    stats: {
      attack: 130,
      critChance: 0.35,
      critDamage: 1.80,
      health: 700,
      defense: 35,
    },
  },
  voidreaper: {
    id: 'voidreaper',
    name: 'Voidreaper',
    icon: '🕳️',
    slot: 'weapon',
    rarity: 'mythic',
    levelReq: 95,
    requirements: { STR: 200, INT: 80 },
    description: 'Harvests souls from beyond the veil.',
    stats: {
      attack: 145,
      critChance: 0.42,
      critDamage: 2.30,
      health: 150,
    },
  },
  cosmicEnder: {
    id: 'cosmicEnder',
    name: 'Cosmic Ender',
    icon: '🌌',
    slot: 'weapon',
    rarity: 'mythic',
    levelReq: 100,
    requirements: { STR: 220, DEX: 100 },
    description: 'The blade that ends universes.',
    stats: {
      attack: 155,
      critChance: 0.38,
      critDamage: 2.60,
    },
  },
  eternityEdge: {
    id: 'eternityEdge',
    name: 'Eternity Edge',
    icon: '⚡',
    slot: 'weapon',
    rarity: 'mythic',
    levelReq: 105,
    requirements: { STR: 250, DEX: 80, INT: 50 },
    description: 'Forged at the end of time itself.',
    stats: {
      attack: 140,
      critChance: 0.48,
      critDamage: 2.00,
      defense: 50,
      speed: 1,
    },
  },
};
